import { compareTimeListIds } from '../time-list/compareIds';
import type { LocalDate } from '@js-joda/core';
import { ZoneId, ZonedDateTime } from '@js-joda/core';
import type { Filing } from '@thinkalpha/quant-ws-client/contracts/filings.js';
import type {
    FilingsPageRequest,
    FilingsPageResponse,
    FilingsPosition,
} from '@thinkalpha/quant-ws-client/contracts/time-list-page.js';
import { createPositionPageAdapter } from '@thinkalpha/ui-symbol-tracked-time-list';
import type { TimeListConfig, TimeListAdapter, SymbolTrackedTimeList } from '@thinkalpha/ui-symbol-tracked-time-list';

export type FilingsQuery = FilingsPageRequest['query'];

export type FilingsTimeList = SymbolTrackedTimeList<
    Filing,
    FilingsQuery,
    FilingsPageRequest,
    FilingsPageResponse,
    FilingsPosition
>;

export const FILINGS_TIME_LIST_CONFIG: TimeListConfig = {
    pageSize: 79,
    retainedWindowRows: 300,
    overscanRows: 20,
    rowHeight: 24,
    separatorHeight: 24,
    bufferHeight: 120,
    pinnedTolerance: 0,
    liveInterval: 10_000,
    leadInterval: 10_000,
};

export function createFilingsAdapter(
    zone = ZoneId.systemDefault(),
): TimeListAdapter<Filing, FilingsQuery, FilingsPageRequest, FilingsPageResponse, FilingsPosition> {
    const adapter = createPositionPageAdapter<Filing, FilingsQuery, FilingsPosition>({
        keyOf: (row) => row.id,
        compare: (a, b) => b.submittedAt.compareTo(a.submittedAt) || compareTimeListIds(b.id, a.id),
        cursorOf: ({ submittedAt, id }) => ({ submittedAt, id }),
        symbolsOf: (row) => [row.symbol],
        calendarDateOf: (row) => ZonedDateTime.ofInstant(row.submittedAt, zone).toLocalDate(),
        declaresLeadSet: false,
    });
    return {
        ...adapter,
        toRequest(instruction) {
            const request = adapter.toRequest(instruction);
            if (request.kind === 'lead') throw new Error('Filings does not support future pages');
            return { ...request, symbols: [...instruction.symbols] } satisfies FilingsPageRequest;
        },
    };
}

/** Date navigation includes the entire selected local calendar day, including fractional seconds. */
export function filingsQuery(
    date: LocalDate | null,
    types: readonly string[],
    tags: readonly string[],
    zone = ZoneId.systemDefault(),
): FilingsQuery {
    return {
        anchor: date === null ? null : date.plusDays(1).atStartOfDay(zone).toInstant().minusNanos(1),
        types: [...types],
        tags: tags.length ? [...tags] : null,
    } satisfies FilingsQuery;
}
